import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { clientAPI, pricingAPI, aiAPI, monetizationAPI } from '../../utils/api';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { isGgfClientCompany } from '../../components/GgfBadge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import TierRoleCombobox from '../../components/TierRoleCombobox';
import { Textarea } from '../../components/ui/textarea';
import { Slider } from '../../components/ui/slider';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Checkbox } from '../../components/ui/checkbox';
import { formatDate } from '../../utils/timeFormat';
import EventTitleField from '../../components/EventTitleField';
import { ArrowLeft, AlertTriangle, Info, Sparkles, Loader2, Zap, Crown, CheckCircle, TrendingUp, X, Eye, MapPin, Shield, Award } from 'lucide-react';
import { toast } from 'sonner';
import InShiftSoftSell from '../../components/InShiftSoftSell';
import { OnsiteDetailsFields, emptyOnsite, onsitePayload } from '../../components/shift/OnsiteDetailsFields';
import GigPreviewModal from '../../components/GigPreviewModal';
import StandbyControls from '../../components/StandbyControls';
import AddressAutocomplete from '../../components/AddressAutocomplete';
import MultiRoleSlotsEditor from '../../components/MultiRoleSlotsEditor';
import PayoutTermsSelector from '../../components/PayoutTermsSelector';

import useClientBusinessRules from '../../hooks/useClientBusinessRules';

const ATTIRE_RESTRICTIONS = [
  'No hats', 'No high heels', 'No ripped clothing', 'No revealing clothing',
  'No open-toe shoes', 'No Crocs or flip flops', 'No tank tops', 'No crop tops',
  'No shorts', 'No baggy clothing', 'No ripped jeans'
];

const GROOMING_REQUIREMENTS = [
  'No visible tattoos (tattoos must be covered)', 'No facial tattoos',
  'No long or dirty fingernails', 'No acrylic or fake nails',
  'No nail polish or colored nails', 'No heavy jewelry (limited jewelry acceptable)',
  'No loose long hair (long hair must be tied up)', 'No extreme hair colors',
  'No unpleasant hygiene (deodorant required)', 'No fake eyelashes'
];

const CreateShift = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  // Iter-254 — GGF certification gate is a client-specific workflow.
  // Only show the "Require GGF Certification" toggle when the logged-in
  // user is Guardian Garage Floors OR an admin (creating on behalf).
  const isGgfClient = isGgfClientCompany(user?.company_name) || isGgfClientCompany(user?.name);
  const isAdminUser = ['admin', 'super_admin'].includes((user?.role || '').toLowerCase());
  const showGgfToggle = isGgfClient || isAdminUser;
  // Iter-311 — client business rules (locked bill rate, role blocks,
  // discovery radius) for the logged-in client.
  const {
    lockedBillRate,
    blockedRoleIds,
    roleReplacements,
  } = useClientBusinessRules(null);
  const isBillRateLocked = lockedBillRate != null;
  const [rolesByTier, setRolesByTier] = useState({
    labor: [],
    hospitality: [],
    premium_activation: []
  });
  const [formData, setFormData] = useState({
    role_id: '',
    city: '',
    state: '',
    venue_name: '',
    venue_address: '',
    venue_zip: '',
    venue_lat: null,
    venue_lng: null,
    payout_type: 'standard',
    payout_terms_business_days: 30,
    payout_expedited: false,
    shift_date: '',
    end_date: '',
    duration_days: 1,
    start_time: '',
    end_time: '',
    is_time_tbd: false,  // Iter-336
    is_date_tbd: false,  // 2026-06 Date TBD
    // Multi-day event controls (client-side)
    is_multi_day: false,
    series_name: '',
    customize_per_day: false,
    per_day_schedules: [],  // [{date, start_time, end_time}]
    package_tier: 'hospitality',
    tier_required: 'flex',
    talent_needed: 1,
    bill_rate: 30,
    // Backup staffing (standby talent — parity with admin flow)
    backups_needed: 0,
    backup_pay_type: 'flat',
    backup_flat_pay: '',
    backup_hourly_rate: '',
    // Iter-190 — Standby / Floater Mode (parity with admin flow)
    standby_enabled: false,
    standby_unlimited: false,
    standby_limit: '',
    standby_pay_amount: '',
    standby_hourly_rate: '',
    standby_description: '',
    standby_notes: '',
    standby_auto_promote: true,
    // Iter-182 — public-facing event title surfaced in Discover, push, email, channels.
    event_title: '',
    // Iter-254 — Creator Network workflow. Auto-toggled when role is
    // Influencer / Content Creator. `creator_type_required` controls
    // which segment of the network gets notified.
    creator_workflow: false,
    creator_type_required: '', // 'influencer'|'content_creator'|'both'
  });
  const [pricingRule, setPricingRule] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingPricing, setLoadingPricing] = useState(false);
  const [description, setDescription] = useState('');
  const [dressCodePreset, setDressCodePreset] = useState('');
  const [customDressCode, setCustomDressCode] = useState('');
  const [dressCodePresets, setDressCodePresets] = useState([]);
  const [aiGenerating, setAiGenerating] = useState(false);
  // Iter-133r — AI generators for dress code + important notes (client side).
  const [aiDressGenerating, setAiDressGenerating] = useState(false);
  const [aiNotesGenerating, setAiNotesGenerating] = useState(false);
  const [eventType, setEventType] = useState('');
  const [featuredBoost, setFeaturedBoost] = useState('none');
  const [featuredPricing, setFeaturedPricing] = useState(null);
  const [jobPostPricing, setJobPostPricing] = useState(null);
  const [rateRecommendation, setRateRecommendation] = useState(null);
  
  // Enhanced gig detail state
  const [duties, setDuties] = useState('');
  const [selectedDressCode, setSelectedDressCode] = useState(null);
  const [curatedDressCodes, setCuratedDressCodes] = useState([]);
  const [curatedAttireRestrictions, setCuratedAttireRestrictions] = useState([]);
  const [curatedGroomingReqs, setCuratedGroomingReqs] = useState([]);
  const [attireRestrictions, setAttireRestrictions] = useState([]);
  const [groomingReqs, setGroomingReqs] = useState([]);
  const [sendHomeWarning, setSendHomeWarning] = useState(false);
  const [additionalNotes, setAdditionalNotes] = useState('');
  // iter417/iter418 — structured on-site / arrival details (Evie reads these).
  const [onsite, setOnsite] = useState(emptyOnsite());
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('none');
  const [showPreview, setShowPreview] = useState(false);
  // Iter-302 — Multi-role booking on the client side. Mirrors the
  // admin AdminCreateShift.js pattern: the primary role uses the
  // single-shift fields above; extras are accumulated here and, on
  // submit, the call routes to `/api/client/events/multi-role`
  // instead of `/api/client/shifts`.
  const [extraRoleSlots, setExtraRoleSlots] = useState([]);
  
  // Certification Requirements
  const [availableCertifications, setAvailableCertifications] = useState([]);
  const [requireCertifications, setRequireCertifications] = useState(false);
  const [selectedCertifications, setSelectedCertifications] = useState([]);

  useEffect(() => {
    fetchRolesByTier();
    fetchDressCodePresets();
    fetchMonetizationPricing();
    fetchTemplates();
    fetchCuratedPresets();
    fetchAvailableCertifications();
  }, []);

  // Build YYYY-MM-DD list between two date strings, inclusive
  const buildDayList = (startStr, endStr) => {
    if (!startStr || !endStr) return [];
    const start = new Date(`${startStr}T00:00:00`);
    const end = new Date(`${endStr}T00:00:00`);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) return [];
    const out = [];
    const cur = new Date(start);
    while (cur <= end) {
      out.push(cur.toISOString().slice(0, 10));
      cur.setDate(cur.getDate() + 1);
    }
    return out;
  };

  // Seed / re-sync per_day_schedules when multi-day + customize-per-day is on.
  // Keeps edited rows, seeds new days with the default times.
  useEffect(() => {
    if (!formData.is_multi_day || !formData.customize_per_day) return;
    const days = buildDayList(formData.shift_date, formData.end_date);
    if (days.length === 0) {
      if ((formData.per_day_schedules || []).length !== 0) {
        setFormData((f) => ({ ...f, per_day_schedules: [] }));
      }
      return;
    }
    setFormData((f) => {
      const byDate = Object.fromEntries((f.per_day_schedules || []).map((e) => [e.date, e]));
      const next = days.map((d) => byDate[d] || {
        date: d,
        // Iter-336 — when TBD is set, seed empty so users must set each day.
        start_time: f.is_time_tbd ? '' : (f.start_time || '09:00'),
        end_time: f.is_time_tbd ? '' : (f.end_time || '17:00'),
      });
      const same = next.length === (f.per_day_schedules || []).length &&
        next.every((n, i) => {
          const p = f.per_day_schedules[i];
          return p && p.date === n.date && p.start_time === n.start_time && p.end_time === n.end_time;
        });
      return same ? f : { ...f, per_day_schedules: next };
    });
  }, [formData.is_multi_day, formData.customize_per_day, formData.shift_date, formData.end_date, formData.start_time, formData.end_time]);

  const updatePerDaySchedule = (idx, field, value) => {
    setFormData((f) => {
      const next = [...(f.per_day_schedules || [])];
      next[idx] = { ...next[idx], [field]: value };
      return { ...f, per_day_schedules: next };
    });
  };

  const resetPerDayToDefault = () => {
    const days = buildDayList(formData.shift_date, formData.end_date);
    setFormData((f) => ({
      ...f,
      per_day_schedules: days.map((d) => ({
        date: d,
        start_time: f.start_time || '09:00',
        end_time: f.end_time || '17:00',
      })),
    }));
    toast.success('Per-day times reset to default');
  };

  useEffect(() => {
    if (formData.city && formData.state && formData.role_id && formData.package_tier) {
      fetchPricingRule();
    }
  }, [formData.city, formData.state, formData.role_id, formData.package_tier]);

  useEffect(() => {
    if (pricingRule) {
      const newWarnings = [];
      if (formData.bill_rate < pricingRule.market_average_bill_rate) {
        newWarnings.push({
          type: 'below_market',
          message: 'This rate is below market and may result in slower fills.',
        });
      }
      setWarnings(newWarnings);
    }
  }, [formData.bill_rate, pricingRule]);

  useEffect(() => {
    // Billing fold is set together with the role via the tier picker; only refresh pricing here.
    setPricingRule(null);
  }, [formData.package_tier]);

  // Iter-254 — Auto-detect Creator Network roles (Influencer / Content
  // Creator). When the picked role triggers the creator workflow, the
  // shift will be matched against verified creators only.
  const _creatorRoleSlug = (() => {
    const all = Object.values(rolesByTier || {}).flat();
    const r = all.find(x => x.id === formData.role_id);
    if (!r) return null;
    const n = String(r.name || '').trim().toLowerCase();
    if (n === 'influencer') return 'influencer';
    if (n === 'content creator') return 'content_creator';
    return null;
  })();
  const isCreatorRole = !!_creatorRoleSlug;

  useEffect(() => {
    if (isCreatorRole) {
      setFormData(prev => ({
        ...prev,
        creator_workflow: true,
        // Default creator_type_required to whatever the picked role is,
        // but let the user override to 'both' when they want hybrid hires.
        creator_type_required: prev.creator_type_required || _creatorRoleSlug,
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        creator_workflow: false,
        creator_type_required: '',
      }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreatorRole, _creatorRoleSlug]);

  const fetchRolesByTier = async () => {
    try {
      const response = await pricingAPI.getRolesByTier();
      setRolesByTier(response.data);
    } catch (error) {
      console.error('Failed to load roles:', error);
      toast.error('Failed to load roles');
    }
  };

  const fetchPricingRule = async () => {
    setLoadingPricing(true);
    try {
      const response = await pricingAPI.getPricingRule({
        city: formData.city,
        state: formData.state,
        role_id: formData.role_id,
        tier_type: formData.package_tier
      });
      
      const rule = response.data;
      setPricingRule(rule);
      
      if (formData.bill_rate === 30 || formData.bill_rate < rule.minimum_bill_rate) {
        setFormData(prev => ({ 
          ...prev, 
          bill_rate: rule.market_average_bill_rate 
        }));
      }
    } catch (error) {
      console.error('Error fetching pricing rule:', error);
      const defaults = {
        labor: {
          minimum_bill_rate: 22,
          recommended_range_low: 25,
          recommended_range_high: 35,
          market_average_bill_rate: 30,
          upper_guidance_cap: 50,
          platform_margin_default: 25,
        },
        hospitality: {
          minimum_bill_rate: 30,
          recommended_range_low: 30,
          recommended_range_high: 50,
          market_average_bill_rate: 40,
          upper_guidance_cap: 70,
          platform_margin_default: 30,
        },
        premium_activation: {
          minimum_bill_rate: 35,
          recommended_range_low: 40,
          recommended_range_high: 50,
          market_average_bill_rate: 45,
          upper_guidance_cap: 50,
          platform_margin_default: 35,
        },
      };
      setPricingRule(defaults[formData.package_tier]);
    } finally {
      setLoadingPricing(false);
    }
  };

  const fetchDressCodePresets = async () => {
    try {
      const response = await aiAPI.getDressCodePresets();
      setDressCodePresets(response.data.presets || []);
    } catch (error) {
      console.error('Failed to load dress code presets:', error);
    }
  };

  const fetchMonetizationPricing = async () => {
    try {
      const [featuredRes, jobPostRes] = await Promise.all([
        monetizationAPI.getFeaturedPricing(),
        monetizationAPI.getJobPostPricing()
      ]);
      setFeaturedPricing(featuredRes.data);
      setJobPostPricing(jobPostRes.data);
    } catch (error) {
      console.error('Failed to load monetization pricing:', error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const res = await import('../../utils/api').then(({ default: api }) => api.get('/templates'));
      setTemplates(res.data || []);
    } catch (error) {
      console.error('Failed to load templates:', error);
    }
  };

  const fetchCuratedPresets = async () => {
    try {
      const res = await import('../../utils/api').then(({ default: api }) => api.get('/presets/all'));
      setCuratedDressCodes(res.data.dress_codes || []);
      setCuratedAttireRestrictions(res.data.attire_restrictions || []);
      setCuratedGroomingReqs(res.data.grooming_requirements || []);
    } catch (error) {
      console.error('Failed to load presets:', error);
    }
  };

  const handleTemplateSelect = (templateId) => {
    setSelectedTemplate(templateId);
    if (templateId === 'none') return;
    const tmpl = templates.find(t => t.id === templateId);
    if (tmpl) {
      // Match dress code by name
      const match = curatedDressCodes.find(dc => dc.name === tmpl.dress_code_name);
      if (match) setSelectedDressCode(match);
      setAttireRestrictions(tmpl.attire_restrictions || []);
      setGroomingReqs(tmpl.grooming_requirements || []);
      setSendHomeWarning(tmpl.send_home_warning || false);
      toast.success(`Template "${tmpl.name}" applied`);
    }
  };

  // Fetch available certifications for job requirements
  const fetchAvailableCertifications = async () => {
    try {
      const res = await api.get('/training/available-certifications');
      setAvailableCertifications(res.data || []);
    } catch (e) {
      console.error('Failed to load certifications:', e);
    }
  };

  // Toggle certification requirement
  const toggleCertification = (certId) => {
    setSelectedCertifications(prev => 
      prev.includes(certId) 
        ? prev.filter(id => id !== certId)
        : [...prev, certId]
    );
  };

  // Fetch rate recommendation when role changes
  useEffect(() => {
    if (formData.role_id) {
      import('../../utils/api').then(({ default: api }) => {
        api.get(`/rates/recommend/${formData.role_id}${formData.bill_rate ? `?bill_rate=${formData.bill_rate}` : ''}`)
          .then(res => setRateRecommendation(res.data))
          .catch(() => setRateRecommendation(null));
      });
    }
  }, [formData.role_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-evaluate rate when bill_rate changes
  useEffect(() => {
    if (formData.role_id && formData.bill_rate) {
      const timer = setTimeout(() => {
        import('../../utils/api').then(({ default: api }) => {
          api.get(`/rates/recommend/${formData.role_id}?bill_rate=${formData.bill_rate}`)
            .then(res => setRateRecommendation(res.data))
            .catch(() => {});
        });
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [formData.bill_rate, formData.role_id]);

  // Iter-311 — when a locked rate is discovered (e.g. SweetWater=$38/hr),
  // snap the bill_rate to that value and keep it snapped on any role
  // change so the slider can't drift.
  useEffect(() => {
    if (isBillRateLocked && formData.bill_rate !== lockedBillRate) {
      setFormData(prev => ({ ...prev, bill_rate: lockedBillRate }));
    }
  }, [isBillRateLocked, lockedBillRate, formData.role_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Iter-311 — if the caller picked a role that's blocked for this client
  // (e.g. Brand Ambassador for SweetWater), auto-remap to the substitute
  // and toast a friendly explanation. Handles legacy templates gracefully.
  useEffect(() => {
    if (formData.role_id && blockedRoleIds.has(formData.role_id)) {
      const target = roleReplacements.get(formData.role_id);
      if (target) {
        setFormData(prev => ({ ...prev, role_id: target }));
        toast.info('This client uses Catering Staff for these bookings — role updated automatically.');
      }
    }
  }, [formData.role_id, blockedRoleIds, roleReplacements]);

  const generateAIDescription = async () => {
    const currentRole = currentRoles.find(r => r.id === formData.role_id);
    if (!currentRole) {
      toast.error('Please select a role first');
      return;
    }
    
    setAiGenerating(true);
    try {
      const response = await aiAPI.generateDescription({
        role_name: currentRole.name,
        event_type: eventType || undefined,
        venue_name: formData.venue_name || undefined,
        city: formData.city || undefined,
        state: formData.state || undefined,
        dress_code_preset: dressCodePreset || undefined,
        custom_dress_code: dressCodePreset === 'custom' ? customDressCode : undefined,
        talent_count: formData.talent_needed || undefined,
      });
      // AI-generated content goes to Duties & Responsibilities AND Description
      const generatedContent = response.data.description;
      setDuties(generatedContent);
      setDescription(generatedContent); // Also populate the description field for visibility
      toast.success('AI description generated! Check Duties & Description fields.');
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to generate description';
      toast.error(msg);
    } finally {
      setAiGenerating(false);
    }
  };

  // Iter-133r — dress code + important notes AI generators.
  const _aiCtx = () => {
    const roleRow = currentRoles.find(r => r.id === formData.role_id);
    return {
      role_name: roleRow?.name,
      event_type: eventType || undefined,
      venue_name: formData.venue_name || undefined,
      city: formData.city || undefined,
      state: formData.state || undefined,
      duties: duties || undefined,
      talent_count: formData.talent_needed || undefined,
    };
  };

  const generateDressCode = async () => {
    const ctx = _aiCtx();
    if (!ctx.role_name) { toast.error('Please select a role first'); return; }
    setAiDressGenerating(true);
    try {
      const res = await aiAPI.generateDressCode({ ...ctx, brand_tone: 'professional + friendly' });
      setCustomDressCode(res.data.dress_code_description || '');
      setDressCodePreset('custom');
      toast.success('AI dress code generated. Edit before saving if needed.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to generate dress code');
    } finally {
      setAiDressGenerating(false);
    }
  };

  const generateImportantNotes = async () => {
    const ctx = _aiCtx();
    if (!ctx.role_name) { toast.error('Please select a role first'); return; }
    setAiNotesGenerating(true);
    try {
      const res = await aiAPI.generateImportantNotes({
        ...ctx,
        dress_code_summary: selectedDressCode?.name || customDressCode || undefined,
      });
      setAdditionalNotes(res.data.important_notes || '');
      toast.success('AI important notes generated. Edit before saving if needed.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to generate important notes');
    } finally {
      setAiNotesGenerating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!pricingRule) {
      toast.error('Please select city, role, and package tier first');
      return;
    }

    if (formData.bill_rate < pricingRule.minimum_bill_rate) {
      toast.error(`Bill rate must be at least $${pricingRule.minimum_bill_rate}/hr`);
      return;
    }

    if (formData.package_tier === 'premium_activation' && formData.bill_rate > 50) {
      toast.error('Bill rate for Premium Activation cannot exceed $50.00/hr');
      return;
    }

    // Validate backup staffing fields
    const _backupsNeeded = parseInt(formData.backups_needed) || 0;
    if (_backupsNeeded > 0) {
      if (formData.backup_pay_type === 'flat' && !parseFloat(formData.backup_flat_pay)) {
        toast.error('Backup Flat Pay is required');
        return;
      }
      if (formData.backup_pay_type === 'hourly' && !parseFloat(formData.backup_hourly_rate)) {
        toast.error('Backup Hourly Rate is required');
        return;
      }
    }

    setLoading(true);

    try {
      const payload = {
        role_id: formData.role_id,
        payout_type: formData.payout_type,
        payout_terms_business_days: formData.payout_terms_business_days,
        payout_expedited: formData.payout_expedited,
        city: formData.city,
        state: formData.state,
        venue_name: formData.venue_name,
        // Iter-182 — event title (optional). Backend validates 5..120 chars when set.
        event_title: (formData.event_title || '').trim() || null,
        venue_address: formData.venue_address,
        venue_lat: formData.venue_lat,
        venue_lng: formData.venue_lng,
        shift_date: formData.shift_date,
        end_date: formData.end_date || formData.shift_date,
        duration_days: parseInt(formData.duration_days) || 1,
        start_time: formData.is_time_tbd ? null : (formData.start_time || null),
        end_time: formData.is_time_tbd ? null : (formData.end_time || null),
        is_time_tbd: !!formData.is_time_tbd,
        is_date_tbd: !!formData.is_date_tbd,
        customize_per_day: !!(formData.is_multi_day && formData.customize_per_day),
        tier_required: formData.tier_required,
        package_tier: formData.package_tier,
        talent_needed: Math.max(1, parseInt(formData.talent_needed, 10) || 1),
        bill_rate: parseFloat(formData.bill_rate),
        // Enhanced gig detail fields - use duties first, fall back to description
        duties: duties || description || null,
        dress_code_name: selectedDressCode?.name || null,
        dress_code_checklist: selectedDressCode?.checklist || [],
        dress_code_description: selectedDressCode?.description || null,
        dress_code_images: selectedDressCode ? [selectedDressCode.image_url] : [],
        attire_restrictions: attireRestrictions,
        grooming_requirements: groomingReqs,
        grooming_images: [],
        additional_notes: additionalNotes || null,
        send_home_warning: sendHomeWarning,
        // iter417/iter418 — structured on-site / arrival details.
        ...onsitePayload(onsite),
        // Certification requirements
        certification_required: requireCertifications && selectedCertifications.length > 0,
        required_certifications: requireCertifications ? selectedCertifications.map(id => ({ module_id: id, required: true })) : [],
        // Iter-245e — Require Guardian Garage Floors certification.
        requires_ggf_certification: !!formData.requires_ggf_certification,
        // Multi-day event fields — only sent when user explicitly enables the toggle
        is_multi_day: !!formData.is_multi_day,
        series_name: formData.is_multi_day ? (formData.series_name || null) : null,
        per_day_schedules: (() => {
          if (!formData.is_multi_day || !formData.customize_per_day) return null;
          const list = formData.per_day_schedules || [];
          if (list.length === 0) return null;
          const defS = formData.start_time;
          const defE = formData.end_time;
          const anyCustom = list.some((e) => e.start_time !== defS || e.end_time !== defE);
          return anyCustom ? list : null;
        })(),
        // Backup staffing — sent only when backups_needed > 0
        backups_needed: _backupsNeeded,
        backup_pay_type: _backupsNeeded > 0 ? formData.backup_pay_type : null,
        backup_flat_pay: _backupsNeeded > 0 && formData.backup_pay_type === 'flat'
          ? parseFloat(formData.backup_flat_pay) : null,
        backup_hourly_rate: _backupsNeeded > 0 && formData.backup_pay_type === 'hourly'
          ? parseFloat(formData.backup_hourly_rate) : null,
        // Iter-190 — Standby / Floater Mode payload
        standby_enabled: !!formData.standby_enabled,
        standby_unlimited: !!formData.standby_unlimited,
        standby_limit: formData.standby_limit && !formData.standby_unlimited
          ? parseInt(formData.standby_limit, 10) : null,
        standby_pay_amount: formData.standby_pay_amount ? parseFloat(formData.standby_pay_amount) : null,
        standby_hourly_rate: formData.standby_hourly_rate ? parseFloat(formData.standby_hourly_rate) : null,
        standby_description: formData.standby_description || null,
        standby_notes: formData.standby_notes || null,
        standby_auto_promote: formData.standby_auto_promote !== false,
        // Iter-254 — Creator Network workflow flags.
        creator_workflow: !!formData.creator_workflow,
        creator_type_required: formData.creator_workflow ? (formData.creator_type_required || null) : null,
      };

      // Iter-302 — Multi-role booking: if the user added extra roles
      // through the MultiRoleSlotsEditor, route the call to the
      // dedicated multi-role endpoint so all roles share one
      // event_group_id. The primary role becomes slot 0; extras
      // follow.
      if (extraRoleSlots && extraRoleSlots.length > 0) {
        const slots = [
          {
            role_id: payload.role_id,
            talent_needed: payload.talent_needed,
            bill_rate: payload.bill_rate,
            package_tier: payload.package_tier,
            start_time: payload.start_time,
            end_time: payload.end_time,
            dress_code: payload.dress_code_name,
            additional_notes: payload.additional_notes,
          },
          ...extraRoleSlots.map((s) => ({
            role_id: s.role_id,
            talent_needed: parseInt(s.talent_needed || 1, 10),
            bill_rate: parseFloat(s.bill_rate || 0),
            package_tier: s.package_tier || payload.package_tier,
            start_time: s.start_time || payload.start_time,
            end_time: s.end_time || payload.end_time,
            talent_pay_rate: s.talent_pay_rate ? parseFloat(s.talent_pay_rate) : undefined,
            dress_code: s.dress_code || null,
            additional_notes: [
              s.additional_notes,
              s.certifications ? `Required certifications: ${s.certifications}` : null,
            ].filter(Boolean).join('\n\n') || null,
          })),
        ];
        await api.post('/client/events/multi-role', {
          city: payload.city,
          state: payload.state,
          venue_name: payload.venue_name,
          venue_address: payload.venue_address,
          venue_lat: payload.venue_lat,
          venue_lng: payload.venue_lng,
          shift_date: payload.shift_date,
          end_date: payload.end_date,
          start_time: payload.start_time,
          end_time: payload.end_time,
          event_title: payload.event_title,
          package_tier: payload.package_tier,
          payout_type: payload.payout_type,
          payout_terms_business_days: payload.payout_terms_business_days,
          payout_expedited: payload.payout_expedited,
          slots,
        });
        toast.success(`Multi-role event created with ${slots.length} roles!`);
        navigate('/client/dashboard');
        return;
      }

      await clientAPI.createShift(payload);
      toast.success('Gig created successfully!');
      navigate('/client/dashboard');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to create gig';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const getPackageTierLabel = (tier) => {
    const labels = {
      labor: 'Labor',
      hospitality: 'Hospitality',
      premium_activation: 'Premium Activation',
    };
    return labels[tier] || tier;
  };

  const getSliderMax = () => {
    if (!pricingRule) return 50;
    if (formData.package_tier === 'premium_activation') return 50;
    return pricingRule.upper_guidance_cap || pricingRule.recommended_range_high + 20;
  };

  const currentRoles = rolesByTier[formData.package_tier] || [];

  return (
    <div className="min-h-screen bg-gray-50" data-testid="create-gig-page">
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/client/dashboard')} 
            className="text-gray-600 hover:text-gray-900"
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button
          onClick={() => navigate('/client/book-with-evie')}
          data-testid="book-with-evie-banner"
          className="w-full mb-5 flex items-center justify-between gap-3 rounded-xl border border-violet-200 bg-gradient-to-r from-violet-50 to-fuchsia-50 px-4 py-3 text-left hover:shadow-md transition-shadow"
        >
          <span className="flex items-center gap-3">
            <span className="w-9 h-9 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center shrink-0">
              <Sparkles className="w-5 h-5 text-white" />
            </span>
            <span>
              <span className="block text-sm font-bold text-violet-900">Book with Evie instead</span>
              <span className="block text-xs text-violet-700">Skip the form — just tell Evie what you need and she'll set it up</span>
            </span>
          </span>
          <span className="text-violet-600 text-sm font-semibold whitespace-nowrap">Try it →</span>
        </button>
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-2xl text-gray-900">Create New Gig</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="create-gig-form">
              {/* Package Tier + Role Selection (8 organizational tiers; billing fold derived from role) */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Package &amp; Role</h3>
                <div className="space-y-2">
                  <TierRoleCombobox
                    value={formData.role_id}
                    context="client_booking"
                    testid="role-select"
                    onChange={(roleId, role) => {
                      setFormData(prev => ({
                        ...prev,
                        role_id: roleId || '',
                        package_tier: role?.billing_fold || prev.package_tier,
                      }));
                    }}
                  />
                  <p className="text-xs text-gray-500">Pick a Package Tier first, then the exact role. Search by role name or alias within that tier.</p>
                </div>
              </div>

              {/* Iter-182 — Event Title (public-facing) */}
              <EventTitleField
                value={formData.event_title}
                onChange={(v) => setFormData({ ...formData, event_title: v })}
                role_name={currentRoles.find(r => r.id === formData.role_id)?.name}
                event_type={formData.package_tier}
                venue_name={formData.venue_name}
                city={formData.city}
                state={formData.state}
                notes={additionalNotes}
              />

              {/* Role Selection */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Role & Location</h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="role_id" className="text-gray-700">Selected Role</Label>
                    <div className="h-10 flex items-center px-3 rounded-md border border-gray-200 bg-gray-50 text-sm text-gray-700" data-testid="selected-role-display">
                      {currentRoles.find(r => r.id === formData.role_id)?.name || 'None selected yet — choose above'}
                    </div>
                  </div>

                  {isCreatorRole && (
                    <div className="space-y-2 col-span-2 bg-purple-50 border border-purple-200 rounded-lg p-4" data-testid="creator-workflow-block">
                      <Label className="text-purple-900 font-semibold">
                        Creator Network · who should see this gig?
                      </Label>
                      <p className="text-xs text-purple-800/80">
                        This gig is part of the Creator Network. Only vetted, verified creators are
                        notified — pick the segment you want.
                      </p>
                      <div className="grid grid-cols-3 gap-2 pt-1">
                        {[
                          { v: 'influencer', label: 'Influencers', sub: 'Audience-driven, paid posts' },
                          { v: 'content_creator', label: 'Content Creators', sub: 'UGC, photo, video, written' },
                          { v: 'both', label: 'Influencer + Creator', sub: 'Hybrid talent' },
                        ].map((opt) => (
                          <button
                            type="button"
                            key={opt.v}
                            onClick={() => setFormData({ ...formData, creator_type_required: opt.v })}
                            data-testid={`creator-type-${opt.v}`}
                            className={
                              'text-left rounded-lg border-2 px-3 py-2 transition ' +
                              (formData.creator_type_required === opt.v
                                ? 'border-purple-500 bg-white shadow-sm'
                                : 'border-purple-200 bg-white hover:border-purple-300')
                            }
                          >
                            <div className="text-sm font-semibold text-purple-900">{opt.label}</div>
                            <div className="text-[11px] text-gray-500 leading-snug">{opt.sub}</div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="talent_needed" className="text-gray-700">Talent Needed *</Label>
                    <Input
                      id="talent_needed"
                      type="number"
                      inputMode="numeric"
                      min="1"
                      value={formData.talent_needed}
                      onChange={(e) => {
                        // Allow temporary empty / partial values while editing —
                        // do NOT snap back to 1 mid-keystroke. Coerce on blur.
                        const raw = e.target.value;
                        setFormData(f => ({
                          ...f,
                          talent_needed: raw === '' ? '' : (parseInt(raw, 10) || 0),
                        }));
                      }}
                      onBlur={(e) => {
                        const n = parseInt(e.target.value, 10);
                        setFormData(f => ({ ...f, talent_needed: Number.isFinite(n) && n >= 1 ? n : 1 }));
                      }}
                      required
                      className="bg-white border-gray-300 text-gray-900"
                      data-testid="talent-needed-input"
                    />
                  </div>
                </div>

                {/* Iter-309 — Additional Staffing Roles now sits BETWEEN
                    Primary Role + headcount and Backup Staffing so the
                    client builds the full staffing plan before choosing
                    backups. (Previously this section lived at the bottom
                    of the form, below Job Posting Pricing.) */}
                <div className="rounded-lg border border-cyan-200 bg-cyan-50/40 p-3 sm:p-4" data-testid="additional-roles-block">
                  <MultiRoleSlotsEditor
                    slots={extraRoleSlots}
                    onChange={setExtraRoleSlots}
                    roles={currentRoles}
                    primaryRoleId={formData.role_id}
                    primaryStartTime={formData.start_time}
                    primaryEndTime={formData.end_time}
                    isMultiDay={!!formData.is_multi_day}
                    eventStartDate={formData.shift_date}
                    eventEndDate={formData.end_date}
                  />
                </div>

                {/* BACKUP STAFFING — standby talent (parity with admin flow) */}
                <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-3 sm:p-4" data-testid="backup-staffing-block">
                  <Label className="font-semibold text-gray-900 mb-1 block">
                    Backup Staffing
                    <span className="text-xs font-normal text-gray-500 ml-2">
                      Standby talent — paid only if activated, or a flat standby fee
                    </span>
                  </Label>
                  <p className="text-[11px] text-gray-500 mb-3">
                    Recommended for high-value events. If a confirmed talent drops, the backup is auto-activated.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Backups Needed</Label>
                      <Input
                        type="number" inputMode="numeric" min="0" max="20"
                        value={formData.backups_needed}
                        onChange={(e) => {
                          const raw = e.target.value;
                          setFormData(f => ({
                            ...f,
                            backups_needed: raw === '' ? '' : (parseInt(raw, 10) || 0),
                          }));
                        }}
                        onBlur={(e) => {
                          const n = parseInt(e.target.value, 10);
                          setFormData(f => ({ ...f, backups_needed: Number.isFinite(n) && n >= 0 ? Math.min(n, 20) : 0 }));
                        }}
                        className="bg-white border-gray-300 text-gray-900"
                        data-testid="client-backups-needed-input"
                      />
                    </div>
                    {(parseInt(formData.backups_needed) || 0) > 0 && (
                      <>
                        <div className="space-y-1.5">
                          <Label className="text-xs">Pay Type</Label>
                          <Select
                            value={formData.backup_pay_type}
                            onValueChange={(v) => setFormData({ ...formData, backup_pay_type: v })}
                          >
                            <SelectTrigger className="bg-white border-gray-300 text-gray-900" data-testid="client-backup-pay-type-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="flat">Flat Rate</SelectItem>
                              <SelectItem value="hourly">Hourly</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1.5">
                          {formData.backup_pay_type === 'flat' ? (
                            <>
                              <Label className="text-xs">Backup Flat Pay ($)</Label>
                              <Input
                                type="number" step="0.01" min="0"
                                value={formData.backup_flat_pay}
                                onChange={(e) => setFormData({ ...formData, backup_flat_pay: e.target.value })}
                                placeholder="e.g. 50.00"
                                className="bg-white border-gray-300 text-gray-900"
                                data-testid="client-backup-flat-pay-input"
                              />
                            </>
                          ) : (
                            <>
                              <Label className="text-xs">Backup Hourly Rate ($/hr)</Label>
                              <Input
                                type="number" step="0.01" min="0"
                                value={formData.backup_hourly_rate}
                                onChange={(e) => setFormData({ ...formData, backup_hourly_rate: e.target.value })}
                                placeholder="e.g. 20.00"
                                className="bg-white border-gray-300 text-gray-900"
                                data-testid="client-backup-hourly-rate-input"
                              />
                            </>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Iter-190 — Standby / Floater Mode controls (client side parity) */}
                <StandbyControls
                  form={formData}
                  update={(k, v) => setFormData((f) => ({ ...f, [k]: v }))}
                />

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="city" className="text-gray-700">City *</Label>
                    <Input
                      id="city"
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      placeholder="Los Angeles"
                      required
                      className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                      data-testid="city-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="state" className="text-gray-700">State *</Label>
                    <Input
                      id="state"
                      value={formData.state}
                      onChange={(e) => setFormData({ ...formData, state: e.target.value.toUpperCase() })}
                      placeholder="CA"
                      maxLength={2}
                      required
                      className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                      data-testid="state-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="venue_zip" className="text-gray-700">Zip</Label>
                    <Input
                      id="venue_zip"
                      inputMode="numeric"
                      value={formData.venue_zip}
                      onChange={(e) => setFormData({ ...formData, venue_zip: e.target.value })}
                      placeholder="90015"
                      maxLength={10}
                      className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                      data-testid="zip-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="venue_name" className="text-gray-700">
                    Venue Name <span className="text-xs text-gray-400">(type a landmark — Google auto-fills the address)</span>
                  </Label>
                  <AddressAutocomplete
                    value={formData.venue_name}
                    onChange={(v) => setFormData({ ...formData, venue_name: v })}
                    onSelect={(data) => {
                      if (!data) return;
                      setFormData(prev => ({
                        ...prev,
                        venue_name: data.name || prev.venue_name,
                        venue_address: data.address || prev.venue_address,
                        city: data.city || prev.city,
                        state: data.state_code || data.state || prev.state,
                        venue_zip: data.postal_code || prev.venue_zip,
                        venue_lat: data.lat,
                        venue_lng: data.lng,
                      }));
                      toast.success('Venue selected — address, city, state, zip auto-filled');
                    }}
                    searchType="establishment"
                    placeholder="e.g. Nashville Fairgrounds, Convention Center…"
                    country="us"
                    data-testid="venue-name-autocomplete"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="venue_address" className="text-gray-700">Venue Address *</Label>
                  <AddressAutocomplete
                    value={formData.venue_address}
                    onChange={(val) => setFormData({ ...formData, venue_address: val })}
                    onSelect={(data) => {
                      if (data) {
                        setFormData(prev => ({
                          ...prev,
                          venue_address: data.address,
                          city: data.city || prev.city,
                          state: data.state_code || data.state || prev.state,
                          venue_zip: data.postal_code || prev.venue_zip,
                          venue_lat: data.lat,
                          venue_lng: data.lng
                        }));
                        toast.success('Address selected — city, state and zip auto-filled');
                      }
                    }}
                    placeholder="Start typing an address..."
                    country="us"
                    required
                    data-testid="venue-address-input"
                  />
                  <p className="text-xs text-gray-500 flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    Pick from suggestions for accurate location, or type manually if Google can't verify.
                  </p>
                  {formData.venue_address && !formData.venue_lat && (
                    <p className="text-xs text-amber-600 flex items-center gap-1" data-testid="address-unverified-warning">
                      <MapPin className="w-3 h-3" />
                      Address not verified — we'll save it as entered. Pick a suggestion to add map coordinates.
                    </p>
                  )}
                </div>
              </div>

              {/* Date and Time */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Schedule</h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="shift_date" className="text-gray-700">Start Date *</Label>
                    <Input
                      id="shift_date"
                      type="date"
                      value={formData.shift_date}
                      onChange={(e) => {
                        const startDate = e.target.value;
                        setFormData(prev => {
                          // If end_date is before new start_date, reset end_date to start_date
                          const newEndDate = prev.end_date && prev.end_date >= startDate 
                            ? prev.end_date 
                            : startDate;
                          // Calculate duration
                          const start = new Date(startDate);
                          const end = new Date(newEndDate);
                          const diffDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
                          return { 
                            ...prev, 
                            shift_date: startDate, 
                            end_date: newEndDate,
                            duration_days: diffDays
                          };
                        });
                      }}
                      required
                      className="bg-white border-gray-300 text-gray-900"
                      data-testid="gig-date-input"
                    />
                    {formData.shift_date && (
                      <p className="text-xs text-gray-500">
                        {formatDate(formData.shift_date)}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="end_date" className="text-gray-700">End Date *</Label>
                    <Input
                      id="end_date"
                      type="date"
                      value={formData.end_date}
                      min={formData.shift_date}
                      onChange={(e) => {
                        const endDate = e.target.value;
                        if (formData.shift_date && endDate >= formData.shift_date) {
                          const start = new Date(formData.shift_date);
                          const end = new Date(endDate);
                          const diffDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
                          setFormData(prev => ({ 
                            ...prev, 
                            end_date: endDate,
                            duration_days: diffDays
                          }));
                        }
                      }}
                      required
                      className="bg-white border-gray-300 text-gray-900"
                      data-testid="gig-end-date-input"
                    />
                    {formData.end_date && (
                      <p className="text-xs text-gray-500">
                        {formatDate(formData.end_date)}
                        {formData.duration_days > 1 && (
                          <span className="text-cyan-600 font-medium ml-2">({formData.duration_days}-day shift)</span>
                        )}
                      </p>
                    )}
                  </div>
                </div>

                {formData.duration_days > 1 && (
                  <div className="rounded-lg border border-cyan-200 bg-cyan-50/60 p-3 sm:p-4 space-y-3" data-testid="client-multi-day-block">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-cyan-900">
                          Multi-Day Event
                        </p>
                        <p className="text-xs text-cyan-700 mt-0.5">
                          Turn this on to create one linked shift per day ({formData.duration_days} total). Attendance, invoices, and payouts will track per day. Leave off to treat the range as a single continuous assignment.
                        </p>
                      </div>
                      <input
                        type="checkbox"
                        checked={!!formData.is_multi_day}
                        onChange={(e) => setFormData((f) => ({ ...f, is_multi_day: e.target.checked }))}
                        className="mt-1 w-5 h-5 accent-cyan-500 cursor-pointer"
                        data-testid="client-multi-day-toggle"
                      />
                    </div>

                    {formData.is_multi_day && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                          <Label className="text-xs text-gray-700">Series Name <span className="text-gray-400">(optional)</span></Label>
                          <Input
                            value={formData.series_name}
                            onChange={(e) => setFormData((f) => ({ ...f, series_name: e.target.value }))}
                            placeholder="e.g. Art Basel Activation"
                            className="bg-white border-gray-300 text-gray-900"
                            data-testid="client-series-name-input"
                          />
                        </div>
                        <div className="flex items-start justify-between bg-white rounded-md border border-cyan-200 p-2.5">
                          <div>
                            <p className="text-xs font-medium text-gray-800">Customize times per day</p>
                            <p className="text-[11px] text-gray-500 mt-0.5">
                              Override start / end time for specific dates.
                            </p>
                          </div>
                          <input
                            type="checkbox"
                            checked={!!formData.customize_per_day}
                            onChange={(e) => setFormData((f) => ({ ...f, customize_per_day: e.target.checked }))}
                            className="mt-1 w-5 h-5 accent-cyan-500 cursor-pointer"
                            data-testid="client-customize-per-day-toggle"
                          />
                        </div>
                      </div>
                    )}

                    {formData.is_multi_day && formData.customize_per_day && (formData.per_day_schedules || []).length > 0 && (
                      <div className="bg-white rounded-md border border-cyan-200 p-3" data-testid="client-per-day-schedule-list">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-[11px] font-medium text-gray-600 uppercase tracking-wide">
                            Per-day schedule ({formData.per_day_schedules.length} day{formData.per_day_schedules.length === 1 ? '' : 's'})
                          </p>
                          <button
                            type="button"
                            onClick={resetPerDayToDefault}
                            className="text-[11px] text-cyan-600 hover:text-cyan-800 underline"
                            data-testid="client-reset-per-day-btn"
                          >
                            Reset to default
                          </button>
                        </div>
                        <div className="space-y-1.5 max-h-[320px] overflow-y-auto pr-1">
                          {formData.per_day_schedules.map((entry, idx) => {
                            const dayLabel = formatDate(entry.date);
                            const isOvernight = entry.end_time && entry.start_time && entry.end_time <= entry.start_time;
                            return (
                              <div
                                key={entry.date}
                                className="grid grid-cols-[1fr_auto_auto] gap-2 items-center bg-gray-50 rounded-md px-2 py-1.5"
                                data-testid={`client-per-day-row-${entry.date}`}
                              >
                                <div className="flex flex-col">
                                  <span className="text-xs font-medium text-gray-800">{dayLabel}</span>
                                  <span className="text-[10px] text-gray-400">{entry.date}</span>
                                  {isOvernight && (
                                    <span className="text-[10px] text-amber-600 mt-0.5">Rolls over to next day</span>
                                  )}
                                </div>
                                <Input
                                  type="time"
                                  value={entry.start_time}
                                  onChange={(e) => updatePerDaySchedule(idx, 'start_time', e.target.value)}
                                  className="h-8 text-sm w-[110px] bg-white border-gray-300"
                                  data-testid={`client-per-day-start-${entry.date}`}
                                />
                                <Input
                                  type="time"
                                  value={entry.end_time}
                                  onChange={(e) => updatePerDaySchedule(idx, 'end_time', e.target.value)}
                                  className="h-8 text-sm w-[110px] bg-white border-gray-300"
                                  data-testid={`client-per-day-end-${entry.date}`}
                                />
                              </div>
                            );
                          })}
                        </div>
                        <p className="text-[10px] text-gray-400 mt-2">
                          Tip: end times earlier than start times roll over to the next day (overnight shifts).
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Iter-336 — TBD toggle + auto-disable when Customize-per-day is ON */}
                {(() => {
                  const perDayOn = !!(formData.is_multi_day && formData.customize_per_day);
                  const disableTop = !!formData.is_time_tbd || perDayOn;
                  return (
                    <>
                      <div className="flex flex-wrap items-center gap-3 text-sm">
                        <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={!!formData.is_time_tbd}
                            disabled={perDayOn}
                            onChange={(e) => setFormData((f) => ({
                              ...f,
                              is_time_tbd: e.target.checked,
                              start_time: e.target.checked ? '' : f.start_time,
                              end_time: e.target.checked ? '' : f.end_time,
                            }))}
                            data-testid="tbd-toggle"
                          />
                          <span className="text-gray-700">Time TBD (times not decided yet)</span>
                        </label>
                        <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={!!formData.is_date_tbd}
                            onChange={(e) => setFormData((f) => ({ ...f, is_date_tbd: e.target.checked }))}
                            data-testid="date-tbd-toggle"
                          />
                          <span className="text-gray-700">Date TBD (date not decided yet)</span>
                        </label>
                        {perDayOn && (
                          <span className="text-xs text-cyan-700">
                            Using per-day schedule below — general times disabled.
                          </span>
                        )}
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="start_time" className={`text-gray-700 ${disableTop ? 'opacity-60' : ''}`}>
                            Start Time{disableTop ? '' : ' *'}
                          </Label>
                          <Input
                            id="start_time"
                            type="time"
                            value={formData.start_time}
                            onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                            required={!disableTop}
                            disabled={disableTop}
                            className="bg-white border-gray-300 text-gray-900 disabled:bg-gray-100 disabled:text-gray-400"
                            data-testid="start-time-input"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="end_time" className={`text-gray-700 ${disableTop ? 'opacity-60' : ''}`}>
                            End Time{disableTop ? '' : ' *'}
                          </Label>
                          <Input
                            id="end_time"
                            type="time"
                            value={formData.end_time}
                            onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                            required={!disableTop}
                            disabled={disableTop}
                            className="bg-white border-gray-300 text-gray-900 disabled:bg-gray-100 disabled:text-gray-400"
                            data-testid="end-time-input"
                          />
                        </div>
                      </div>
                    </>
                  );
                })()}
              </div>

              {/* AI Gig Description Builder */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-semibold text-gray-900">Gig Description</h3>
                  <Badge className="bg-cyan-100 text-cyan-700 border-0 text-xs">AI-Powered</Badge>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-gray-700">Event Type</Label>
                    <Input
                      value={eventType}
                      onChange={(e) => setEventType(e.target.value)}
                      placeholder="e.g. Product Launch, Corporate Gala"
                      className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                      data-testid="event-type-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-700">Dress Code</Label>
                    <Select value={dressCodePreset} onValueChange={setDressCodePreset}>
                      <SelectTrigger className="bg-white border-gray-300 text-gray-900" data-testid="dress-code-select">
                        <SelectValue placeholder="Select dress code" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-gray-200">
                        {dressCodePresets.map((preset) => (
                          <SelectItem key={preset.key} value={preset.key}>
                            {preset.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {dressCodePreset === 'custom' && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-gray-700">Custom Dress Code Details</Label>
                      <Button
                        type="button" size="sm" variant="outline"
                        onClick={generateDressCode}
                        disabled={aiDressGenerating || !formData.role_id}
                        className="border-cyan-300 text-cyan-700 hover:bg-cyan-50"
                        data-testid="client-generate-dress-code-btn"
                      >
                        {aiDressGenerating ? (
                          <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Generating…</>
                        ) : (
                          <><Sparkles className="w-3.5 h-3.5 mr-1.5" />Generate with AI</>
                        )}
                      </Button>
                    </div>
                    <Textarea
                      value={customDressCode}
                      onChange={(e) => setCustomDressCode(e.target.value)}
                      placeholder="Describe the required dress code, or click Generate with AI."
                      rows={3}
                      className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                      data-testid="custom-dress-code-input"
                    />
                  </div>
                )}

                {dressCodePreset && dressCodePreset !== 'custom' && (
                  <p className="text-xs text-gray-500 px-1">
                    {dressCodePresets.find(p => p.key === dressCodePreset)?.description}
                  </p>
                )}

                {/* Duties & Responsibilities - AI-Powered */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Label className="text-gray-700">Duties & Responsibilities</Label>
                      <Badge className="bg-cyan-100 text-cyan-700 border-0 text-xs">AI-Powered</Badge>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={generateAIDescription}
                      disabled={aiGenerating || !formData.role_id}
                      className="border-cyan-300 text-cyan-700 hover:bg-cyan-50"
                      data-testid="generate-duties-btn"
                    >
                      {aiGenerating ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 mr-1" />
                          Generate with AI
                        </>
                      )}
                    </Button>
                  </div>
                  <Textarea
                    value={duties}
                    onChange={(e) => setDuties(e.target.value)}
                    placeholder="List the duties and responsibilities for this role, or click 'Generate with AI' to auto-create..."
                    rows={4}
                    className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                    data-testid="duties-textarea"
                  />
                </div>

                {/* Curated Dress Code Selection */}
                <div className="space-y-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center justify-between">
                    <Label className="text-gray-900 font-semibold">Dress Code</Label>
                    {templates.length > 0 && (
                      <Select value={selectedTemplate} onValueChange={handleTemplateSelect}>
                        <SelectTrigger className="w-48 bg-white text-sm" data-testid="template-select">
                          <SelectValue placeholder="Use template..." />
                        </SelectTrigger>
                        <SelectContent className="bg-white">
                          <SelectItem value="none">No template</SelectItem>
                          {templates.map(t => (
                            <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {curatedDressCodes.map((dc) => (
                      <div
                        key={dc.id}
                        className={`relative cursor-pointer rounded-lg border-2 overflow-hidden transition-all ${
                          selectedDressCode?.id === dc.id
                            ? 'border-cyan-500 ring-2 ring-cyan-200'
                            : 'border-gray-200 hover:border-gray-400'
                        }`}
                        onClick={() => setSelectedDressCode(selectedDressCode?.id === dc.id ? null : dc)}
                        data-testid={`dress-code-${dc.id}`}
                      >
                        <img src={dc.image_url} alt={dc.name} className="w-full h-32 object-cover" />
                        <div className="p-2 bg-white">
                          <p className="text-xs font-semibold text-gray-900 truncate">{dc.name}</p>
                        </div>
                        {selectedDressCode?.id === dc.id && (
                          <div className="absolute top-2 right-2 w-6 h-6 bg-cyan-500 rounded-full flex items-center justify-center">
                            <CheckCircle className="w-4 h-4 text-white" />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  {selectedDressCode && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-2">
                      <p className="text-sm font-medium text-blue-900">{selectedDressCode.name}</p>
                      <p className="text-xs text-blue-700 mt-1">{selectedDressCode.description}</p>
                      <ul className="mt-2 space-y-1">
                        {selectedDressCode.checklist?.map((item, i) => (
                          <li key={i} className="flex items-center gap-1.5 text-xs text-blue-800">
                            <CheckCircle className="w-3 h-3 text-blue-500 shrink-0" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {/* Attire Restrictions */}
                <div className="space-y-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <Label className="text-gray-900 font-semibold">Attire Restrictions</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {curatedAttireRestrictions.map(r => (
                      <label key={r.id} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`attire-${r.id}`}>
                        <input
                          type="checkbox"
                          checked={attireRestrictions.includes(r.label)}
                          onChange={(e) => {
                            if (e.target.checked) setAttireRestrictions([...attireRestrictions, r.label]);
                            else setAttireRestrictions(attireRestrictions.filter(x => x !== r.label));
                          }}
                          className="rounded border-gray-300 text-cyan-500"
                        />
                        <span className="text-gray-700">{r.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Grooming Requirements */}
                <div className="space-y-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <Label className="text-gray-900 font-semibold">Grooming Requirements</Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {curatedGroomingReqs.map(r => (
                      <label key={r.id} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`grooming-${r.id}`}>
                        <input
                          type="checkbox"
                          checked={groomingReqs.includes(r.label)}
                          onChange={(e) => {
                            if (e.target.checked) setGroomingReqs([...groomingReqs, r.label]);
                            else setGroomingReqs(groomingReqs.filter(x => x !== r.label));
                          }}
                          className="rounded border-gray-300 text-cyan-500"
                        />
                        <span className="text-gray-700">{r.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Send Home Warning */}
                <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <Switch
                    checked={sendHomeWarning}
                    onCheckedChange={setSendHomeWarning}
                    data-testid="send-home-warning-switch"
                  />
                  <div>
                    <p className="text-sm font-medium text-amber-900">Send Home Warning</p>
                    <p className="text-xs text-amber-700">Display: "This partner may send you home if these requirements are not met."</p>
                  </div>
                </div>

                {/* Additional Notes */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-gray-700">Additional Notes & Instructions</Label>
                    <Button
                      type="button" size="sm" variant="outline"
                      onClick={generateImportantNotes}
                      disabled={aiNotesGenerating || !formData.role_id}
                      className="border-cyan-300 text-cyan-700 hover:bg-cyan-50"
                      data-testid="client-generate-important-notes-btn"
                    >
                      {aiNotesGenerating ? (
                        <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Generating…</>
                      ) : (
                        <><Sparkles className="w-3.5 h-3.5 mr-1.5" />Generate with AI</>
                      )}
                    </Button>
                  </div>
                  <Textarea
                    value={additionalNotes}
                    onChange={(e) => setAdditionalNotes(e.target.value)}
                    placeholder="Any additional instructions for talent..."
                    rows={3}
                    className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                    data-testid="additional-notes-textarea"
                  />
                </div>

                {/* iter417/iter418 — Onsite / Arrival Details (Evie reads these) */}
                <div className="space-y-2 bg-blue-50/50 border border-blue-200 rounded-lg p-3" data-testid="client-onsite-details-block">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-blue-600" />
                    <Label className="text-gray-800 font-semibold">Onsite / Arrival Details</Label>
                  </div>
                  <p className="text-xs text-gray-500">
                    Booth #, reporting location, parking and maps. Talent and Evie use these to know exactly where to go.
                  </p>
                  <OnsiteDetailsFields
                    idPrefix="client-create-onsite"
                    value={onsite}
                    onChange={(k, v) => setOnsite((o) => ({ ...o, [k]: v }))}
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-gray-700">Description</Label>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={generateAIDescription}
                      disabled={aiGenerating || !formData.role_id}
                      className="border-cyan-300 text-cyan-700 hover:bg-cyan-50"
                      data-testid="ai-generate-btn"
                    >
                      {aiGenerating ? (
                        <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Generating...</>
                      ) : (
                        <><Sparkles className="w-3.5 h-3.5 mr-1.5" />Generate with AI</>
                      )}
                    </Button>
                  </div>
                  <Textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Describe the gig, expectations, and any special requirements. Use the AI generator above for a head start."
                    rows={6}
                    className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                    data-testid="description-textarea"
                  />
                </div>
              </div>

              {/* Talent Requirements - Certifications */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-purple-500" />
                  Talent Requirements
                </h3>
                
                <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <div>
                    <p className="font-medium text-gray-900">Require Certifications</p>
                    <p className="text-sm text-gray-600">Only certified talent can apply to this shift</p>
                  </div>
                  <Switch
                    checked={requireCertifications}
                    onCheckedChange={setRequireCertifications}
                    data-testid="require-certifications-toggle"
                  />
                </div>

                {/* Iter-245e / Iter-254 — GGF certification gate. Only
                    surfaced when the client is Guardian Garage Floors
                    or when admin is creating on behalf. Other clients
                    do NOT see GGF anywhere. */}
                {showGgfToggle && (
                  <div className="flex items-center justify-between p-4 bg-cyan-50 rounded-lg border border-cyan-200">
                    <div className="pr-3">
                      <p className="font-medium text-gray-900 flex items-center gap-2">
                        <Shield className="w-4 h-4 text-cyan-600" />
                        Require GGF Certification
                      </p>
                      <p className="text-sm text-gray-600">
                        Only Guardian Garage Floors-certified talent may apply.
                        Non-certified talent will be directed to complete the
                        in-app GGF assessment before applying.
                      </p>
                    </div>
                    <Switch
                      checked={!!formData.requires_ggf_certification}
                      onCheckedChange={(v) => setFormData({ ...formData, requires_ggf_certification: v })}
                      data-testid="require-ggf-certification-toggle"
                    />
                  </div>
                )}
                
                {requireCertifications && (
                  <div className="space-y-3">
                    <Label className="text-gray-700">Select Required Certifications</Label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {availableCertifications.map(cert => (
                        <div
                          key={cert.id}
                          onClick={() => toggleCertification(cert.id)}
                          className={`p-3 rounded-lg border cursor-pointer transition-all ${
                            selectedCertifications.includes(cert.id)
                              ? 'bg-purple-100 border-purple-400 ring-2 ring-purple-200'
                              : 'bg-white border-gray-200 hover:border-purple-300'
                          }`}
                          data-testid={`cert-option-${cert.id}`}
                        >
                          <div className="flex items-center gap-3">
                            <Checkbox
                              checked={selectedCertifications.includes(cert.id)}
                              onCheckedChange={() => toggleCertification(cert.id)}
                              className="pointer-events-none"
                            />
                            <div className="flex-1">
                              <p className="font-medium text-gray-900 flex items-center gap-2">
                                <Award className="w-4 h-4 text-purple-500" />
                                {cert.badge_name}
                              </p>
                              <p className="text-xs text-gray-500">{cert.category_name}</p>
                            </div>
                            {cert.is_safety_critical && (
                              <Badge variant="destructive" className="text-xs">Safety</Badge>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {selectedCertifications.length > 0 && (
                      <Alert className="bg-purple-50 border-purple-200">
                        <Shield className="w-4 h-4 text-purple-600" />
                        <AlertDescription className="text-purple-900">
                          <p className="font-medium">{selectedCertifications.length} certification(s) required</p>
                          <p className="text-sm">Only talent with these certifications can apply. This helps ensure quality and compliance.</p>
                        </AlertDescription>
                      </Alert>
                    )}
                  </div>
                )}
              </div>

              {/* Pricing with Slider */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Pricing</h3>
                
                {loadingPricing ? (
                  <div className="text-center py-4 text-gray-500">Loading pricing data...</div>
                ) : pricingRule ? (
                  <>
                    <Alert className="bg-cyan-50 border-cyan-300">
                      <Info className="h-4 w-4 text-cyan-600" />
                      <AlertDescription className="text-cyan-900">
                        <div className="space-y-1">
                          <p className="font-medium">Market Guidance for {formData.city || 'your location'}</p>
                          <p className="text-sm">Market Average: <strong>${pricingRule.market_average_bill_rate}/hr</strong></p>
                          <p className="text-sm">Recommended Range: ${pricingRule.recommended_range_low} - ${pricingRule.recommended_range_high}/hr</p>
                          {formData.package_tier === 'premium_activation' && (
                            <p className="text-sm text-cyan-700">Maximum: $50.00/hr (Premium Activation cap)</p>
                          )}
                        </div>
                      </AlertDescription>
                    </Alert>

                    {warnings.length > 0 && (
                      <Alert className="bg-amber-50 border-amber-300">
                        <AlertTriangle className="h-4 w-4 text-amber-600" />
                        <AlertDescription className="text-amber-900">
                          {warnings[0].message}
                        </AlertDescription>
                      </Alert>
                    )}

                    <div className="space-y-4">
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <Label className="text-gray-700 flex items-center gap-2">
                            Bill Rate ($/hr) *
                            {isBillRateLocked && (
                              <span
                                data-testid="bill-rate-locked-badge"
                                className="inline-flex items-center gap-1 rounded-full bg-cyan-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-cyan-800 border border-cyan-200"
                                title={`Client-negotiated rate — $${Number(lockedBillRate).toFixed(2)}/hr. Contact your account manager to change.`}
                              >
                                <Shield size={10} strokeWidth={2.5} /> Locked
                              </span>
                            )}
                          </Label>
                          <span className="text-2xl font-bold text-cyan-600" data-testid="bill-rate-display">
                            ${formData.bill_rate.toFixed(2)}/hr
                          </span>
                        </div>
                        <Slider
                          value={[formData.bill_rate]}
                          onValueChange={([value]) => !isBillRateLocked && setFormData({ ...formData, bill_rate: value })}
                          min={pricingRule.minimum_bill_rate}
                          max={getSliderMax()}
                          step={0.50}
                          disabled={isBillRateLocked}
                          className={`py-4 ${isBillRateLocked ? 'opacity-50 pointer-events-none' : ''}`}
                          data-testid="bill-rate-slider"
                        />
                        <div className="flex justify-between text-xs text-gray-500">
                          <span>Min: ${pricingRule.minimum_bill_rate}</span>
                          <span className="text-cyan-600 font-medium">Market: ${pricingRule.market_average_bill_rate}</span>
                          <span>Max: ${getSliderMax()}</span>
                        </div>

                        {/* Iter-311 — locked-rate banner replaces the AI recommendation card */}
                        {isBillRateLocked ? (
                          <div
                            className="mt-3 rounded-lg p-3 border bg-cyan-50 border-cyan-200"
                            data-testid="rate-locked-banner"
                          >
                            <div className="flex items-start gap-2">
                              <Shield className="w-4 h-4 text-cyan-600 shrink-0 mt-0.5" />
                              <div>
                                <p className="text-sm font-medium text-cyan-900">
                                  Locked client rate — ${Number(lockedBillRate).toFixed(2)}/hr
                                </p>
                                <p className="text-xs text-cyan-800 mt-0.5">
                                  This client has a negotiated rate. Contact your account manager to request a change.
                                </p>
                              </div>
                            </div>
                          </div>
                        ) : (
                        /* Smart Rate Recommendation */
                        rateRecommendation?.rate_assessment && (
                          <div className={`mt-3 rounded-lg p-3 border ${
                            rateRecommendation.rate_assessment.severity === 'error' ? 'bg-red-50 border-red-200' :
                            rateRecommendation.rate_assessment.severity === 'warning' ? 'bg-amber-50 border-amber-200' :
                            'bg-green-50 border-green-200'
                          }`} data-testid="rate-recommendation">
                            <div className="flex items-start gap-2">
                              {rateRecommendation.rate_assessment.severity === 'success' ? (
                                <CheckCircle className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
                              ) : rateRecommendation.rate_assessment.severity === 'error' ? (
                                <AlertTriangle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                              ) : (
                                <TrendingUp className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                              )}
                              <div>
                                <p className={`text-sm ${
                                  rateRecommendation.rate_assessment.severity === 'error' ? 'text-red-700' :
                                  rateRecommendation.rate_assessment.severity === 'warning' ? 'text-amber-700' :
                                  'text-green-700'
                                }`}>{rateRecommendation.rate_assessment.message}</p>

                                {rateRecommendation.suggested_actions?.length > 0 && (
                                  <div className="flex flex-wrap gap-2 mt-2">
                                    {rateRecommendation.suggested_actions.map((action, i) => (
                                      <Button
                                        key={i}
                                        size="sm"
                                        variant={action.type === 'recommended' ? 'default' : 'outline'}
                                        className={action.type === 'recommended' ? 'bg-cyan-500 hover:bg-cyan-600 text-white h-7 text-xs' : 'h-7 text-xs'}
                                        onClick={() => setFormData({ ...formData, bill_rate: action.value })}
                                        data-testid={`rate-action-${action.type}`}
                                      >
                                        {action.label}
                                      </Button>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        )
                        )}

                        {/* Talent payout tiers hidden - admin-only info */}
                      </div>

                      <div className="bg-gray-50 rounded-lg p-4 text-sm border border-gray-200">
                        <p className="text-gray-600">Package: <span className="text-gray-900 font-medium">{getPackageTierLabel(formData.package_tier)}</span></p>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-4 text-gray-500">
                    Select city, state, and role to see pricing guidance
                  </div>
                )}
              </div>

              <div className="pt-4 border-t border-gray-200 space-y-6">
                {/* Featured Job Boost */}
                {featuredPricing && (
                  <div className="w-full">
                    <div className="flex items-center gap-2 mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">Featured Job Boost</h3>
                      <Badge className="bg-purple-100 text-purple-700 border-0 text-xs">Optional</Badge>
                    </div>
                    <p className="text-sm text-gray-600 mb-4">
                      Get more applicants by featuring your gig at the top of talent listings with a Featured badge.
                    </p>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {/* No Boost */}
                      <Card
                        className={`cursor-pointer transition-all border-2 ${
                          featuredBoost === 'none' 
                            ? 'border-gray-400 bg-gray-100 shadow-sm' 
                            : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
                        }`}
                        onClick={() => setFeaturedBoost('none')}
                        data-testid="boost-none"
                      >
                        <CardContent className="p-4 text-center">
                          <p className="font-semibold text-gray-800 mb-1">No Boost</p>
                          <p className="text-2xl font-bold text-gray-900">$0</p>
                          <p className="text-xs text-gray-500 mt-1">Standard listing</p>
                        </CardContent>
                      </Card>

                      {/* Boost Options */}
                      {featuredPricing.options.map((opt) => (
                        <Card
                          key={opt.id}
                          className={`cursor-pointer transition-all border-2 relative ${
                            featuredBoost === opt.id
                              ? 'border-purple-400 bg-purple-50 shadow-md'
                              : 'border-gray-200 bg-white hover:border-purple-200 hover:shadow-sm'
                          }`}
                          onClick={() => setFeaturedBoost(opt.id)}
                          data-testid={`boost-${opt.id}`}
                        >
                          {opt.best_value && (
                            <div className="absolute -top-2 left-1/2 -translate-x-1/2 bg-purple-600 text-white text-[9px] font-bold px-2 py-0.5 rounded-full shadow-sm whitespace-nowrap">
                              BEST VALUE
                            </div>
                          )}
                          <CardContent className="p-4 text-center">
                            <p className="font-semibold text-purple-700 mb-1 whitespace-nowrap">{opt.name}</p>
                            <p className="text-2xl font-bold text-gray-900">${opt.price}</p>
                            <p className="text-xs text-gray-600 mt-1">{opt.duration_days} days featured</p>
                          </CardContent>
                        </Card>
                      ))}
                    </div>

                    {featuredBoost !== 'none' && (
                      <div className="mt-3 flex items-center gap-2 text-sm text-purple-800 bg-purple-50 border border-purple-200 rounded-lg p-3">
                        <Sparkles className="w-4 h-4 shrink-0 text-purple-600" />
                        <span>Your job will appear at the top of listings with a <strong>Featured</strong> badge</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Job Post Pricing + Pro Upsell side-by-side on wide screens */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* Job Post Pricing Summary */}
                  {jobPostPricing && (
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <h4 className="text-sm font-semibold text-gray-700 mb-3">Job Posting Pricing</h4>
                      <div className="grid grid-cols-2 gap-3 mb-3">
                        <div className="bg-white rounded-lg p-3 border border-gray-200">
                          <p className="text-xs text-gray-500">Local Post</p>
                          <p className="text-lg font-bold text-gray-900">${jobPostPricing.per_job.local.price}</p>
                          <p className="text-[10px] text-gray-400">Visible in your area</p>
                        </div>
                        <div className="bg-white rounded-lg p-3 border border-gray-200">
                          <p className="text-xs text-gray-500">National Post</p>
                          <p className="text-lg font-bold text-gray-900">${jobPostPricing.per_job.national.price}</p>
                          <p className="text-[10px] text-gray-400">Visible nationwide</p>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 flex items-center gap-1">
                        <Info className="w-3 h-3" />
                        Or subscribe to save on recurring posts.
                        {' '}<span className="text-cyan-600 font-medium">Plans start at ${jobPostPricing.subscriptions[0]?.price}/mo</span>
                      </p>
                    </div>
                  )}

                  {/* Pro Upgrade Soft-Sell — shows only for pay-per-job clients
                      when the savings vs Pro crosses $20 this month. Deliberately
                      inline + non-blocking. */}
                  <InShiftSoftSell />
                </div>

                {/* 2026-06 — Required per-gig Payout Terms (client-facing). */}
                <div className="border-t pt-4">
                  <PayoutTermsSelector
                    value={{
                      payout_type: formData.payout_type,
                      payout_terms_business_days: formData.payout_terms_business_days,
                      payout_expedited: formData.payout_expedited,
                    }}
                    onChange={(nv) => setFormData((f) => ({ ...f, ...nv }))}
                  />
                </div>

                {/* Final action buttons — pinned right on their own row */}
                <div className="flex flex-wrap justify-end gap-3 pt-2">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => navigate('/client/dashboard')}
                    className="border-gray-300 text-gray-700 hover:bg-gray-100"
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="button"
                    variant="outline"
                    onClick={() => setShowPreview(true)}
                    className="border-cyan-300 text-cyan-700 hover:bg-cyan-50"
                    data-testid="preview-gig-button"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Preview
                  </Button>
                  <Button 
                    type="submit" 
                    disabled={loading || !pricingRule || !formData.role_id} 
                    className="bg-cyan-500 hover:bg-cyan-600 text-white" 
                    data-testid="submit-gig-button"
                  >
                    {loading ? 'Creating...' : (extraRoleSlots.length > 0 ? `Create Event (${extraRoleSlots.length + 1} roles)` : 'Create Gig')}
                  </Button>
                </div>
              </div>

              {/* Iter-302 — Multi-role booking editor moved up below
                  Primary Role section. Kept stub here in case any
                  external integration depended on this anchor. */}
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Gig Preview Modal */}
      <GigPreviewModal
        open={showPreview}
        onClose={() => setShowPreview(false)}
        role_name={currentRoles.find(r => r.id === formData.role_id)?.name || ''}
        venue_name={formData.venue_name}
        venue_address={formData.venue_address}
        city={formData.city}
        state={formData.state}
        shift_date={formData.shift_date}
        start_time={formData.start_time}
        end_time={formData.end_time}
        hourly_rate={pricingRule ? (formData.bill_rate * (1 - (pricingRule.platform_margin_default || 30) / 100)).toFixed(2) : '0'}
        talent_needed={formData.talent_needed}
        tier_required={formData.tier_required}
        duties={duties}
        dress_code_name={selectedDressCode?.name}
        dress_code_checklist={selectedDressCode?.checklist || []}
        dress_code_description={selectedDressCode?.description}
        dress_code_images={selectedDressCode ? [selectedDressCode.image_url] : []}
        attire_restrictions={attireRestrictions}
        grooming_requirements={groomingReqs}
        grooming_images={[]}
        send_home_warning={sendHomeWarning}
        additional_notes={additionalNotes}
      />
    </div>
  );
};

export default CreateShift;
